/**
 * API base URLs for the portal frontend.
 *
 * Deployed builds use same-origin /api/* paths (nginx proxies to Cloud Run backend)
 * so Safari and other strict browsers never call *.run.app directly.
 * Local CRA dev continues to talk to localhost:8080.
 */

const LOCAL_CONTRACT_API = 'http://localhost:8080/contract';
const SAME_ORIGIN_CONTRACT_API = '/api/contract';

export function isLocalDevHostname(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function trimUrl(value) {
  return (value || '').trim().replace(/\/$/, '');
}

export function getContractApiUrl() {
  const fromEnv = trimUrl(process.env.REACT_APP_BACKEND_URL);
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window !== 'undefined' && isLocalDevHostname(window.location.hostname)) {
    return LOCAL_CONTRACT_API;
  }
  return SAME_ORIGIN_CONTRACT_API;
}

/** Portal download URL for a split/attachment, always on this frontend's contract API. */
export function emailAttachmentDownloadUrl(
  attachmentId,
  { type = 'file', source = 'split', userEmail, action } = {},
) {
  const id = Number(attachmentId);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  const base = getContractApiUrl();
  const contractBase = base.endsWith('/contract') ? base : `${base}/contract`;
  const params = new URLSearchParams({ type });
  if (source && source !== 'attachment') {
    params.set('source', source);
  }
  if (userEmail) {
    params.set('user_email', userEmail);
  }
  if (action) {
    params.set('action', action);
  }
  return `${contractBase}/email-attachments/${id}/download?${params.toString()}`;
}

function replaceContractSuffix(suffix) {
  const base = getContractApiUrl();
  if (base.endsWith('/contract')) {
    return suffix ? base.replace(/\/contract$/, suffix) : base.replace(/\/contract$/, '');
  }
  return suffix ? `${base}${suffix}` : base;
}

export function getListingsApiUrl() {
  return replaceContractSuffix('/listings');
}

export function getReferralsApiUrl() {
  return replaceContractSuffix('/referrals');
}

export function getTmsApiUrl() {
  return replaceContractSuffix('/tms');
}

export function getAdminAssistantApiUrl() {
  return replaceContractSuffix('/admin-assistant');
}

export function getBackendBaseUrl() {
  return replaceContractSuffix('');
}

export function getDealReplaceApiUrl() {
  return `${getBackendBaseUrl()}/deal-replace`;
}

export function getGmailApiUrl() {
  const fromEnv = trimUrl(process.env.REACT_APP_GMAIL_URL);
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window !== 'undefined' && isLocalDevHostname(window.location.hostname)) {
    return 'http://localhost:8080/email';
  }
  return '/api/email';
}

export function getLoanTimelineApiUrl() {
  const fromEnv = trimUrl(process.env.REACT_APP_LOAN_TIMELINE_URL);
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window !== 'undefined' && isLocalDevHostname(window.location.hostname)) {
    return 'http://localhost:8080/loan';
  }
  return '/api/loan';
}

export function getInspectionTimelineApiUrl() {
  const fromEnv = trimUrl(process.env.REACT_APP_INSPECTION_TIMELINE_URL);
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window !== 'undefined' && isLocalDevHostname(window.location.hostname)) {
    return 'http://localhost:8080/inspection';
  }
  return '/api/inspection';
}

/**
 * True when `url` targets the portal backend API (any of the configured API bases).
 * Used by the global bearer-injecting axios request interceptor (AuthSessionGuard) so
 * access tokens are attached only to our own API calls — never to Auth0, Stripe, or
 * other third-party requests. Absolute URLs must match a configured API origin (SPA
 * origin or localhost:8080 in CRA dev); pathname must match a configured API base.
 */
function configuredApiOrigins() {
  const origins = new Set();
  if (typeof window !== 'undefined') {
    origins.add(window.location.origin);
  }
  for (const base of [
    getBackendBaseUrl(),
    getContractApiUrl(),
    getAdminAssistantApiUrl(),
    getGmailApiUrl(),
    getLoanTimelineApiUrl(),
    getInspectionTimelineApiUrl(),
  ]) {
    const trimmed = (base || '').trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      try {
        origins.add(new URL(trimmed).origin);
      } catch {
        // ignore malformed env override
      }
    }
  }
  return origins;
}

function parseApiTarget(url) {
  const target = (url || '').trim();
  if (!target) {
    return null;
  }
  if (target.startsWith('http://') || target.startsWith('https://')) {
    try {
      const parsed = new URL(target);
      return { origin: parsed.origin, path: `${parsed.pathname}${parsed.search}` };
    } catch {
      return null;
    }
  }
  return { origin: null, path: target };
}

function normalizeApiBase(base) {
  const b = (base || '').trim();
  if (!b) {
    return null;
  }
  if (b.startsWith('http://') || b.startsWith('https://')) {
    try {
      const parsed = new URL(b);
      const path = parsed.pathname.replace(/\/$/, '') || '/';
      return path;
    } catch {
      return b.replace(/\/$/, '');
    }
  }
  return b.replace(/\/$/, '');
}

export function isPortalApiUrl(url) {
  const parsed = parseApiTarget(url);
  if (!parsed) {
    return false;
  }
  if (parsed.origin !== null && !configuredApiOrigins().has(parsed.origin)) {
    return false;
  }
  const bases = [
    getBackendBaseUrl(),
    getContractApiUrl(),
    getAdminAssistantApiUrl(),
    getGmailApiUrl(),
    getLoanTimelineApiUrl(),
    getInspectionTimelineApiUrl(),
  ].filter(Boolean).map(normalizeApiBase).filter(Boolean);
  return bases.some((base) => parsed.path === base || parsed.path.startsWith(`${base}/`));
}

/** Auth0 redirect/callback origin — pinned to portal domain in production builds. */
export function getAuth0RedirectUri() {
  const configured = trimUrl(process.env.REACT_APP_PORTAL_ORIGIN);
  if (configured) {
    return configured;
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

/** True when the frontend was built for production (REACT_APP_ENVIRONMENT_NAME=Production). */
export function isProductionEnvironment() {
  return (process.env.REACT_APP_ENVIRONMENT_NAME || '').trim() === 'Production';
}
